import { createStore } from '@stencil/store'

import { IBookmark, isImmutable } from '../../../bookmarks/types'
import { INote } from '../../../call/types'
import { useLogging } from '../../../utils/logging'
import { PlaybackStore, TranscriptCue, TranscriptStore } from './state'

export const AnnotationStore = createStore({
  selectedNote: null as INote,
  startCue: null as TranscriptCue,
  endCue: null as TranscriptCue,
  selectedCues: null as TranscriptCue[],
  selectableCues: null as TranscriptCue[],
  processingNote: null as INote,
  processingCue: null as TranscriptCue,
  processingBookmark: null as IBookmark,
  isUpdating: false,
  editCue: null as TranscriptCue,
  trimCue: null as TranscriptCue,
})

const { state, onChange } = AnnotationStore

onChange('editCue', (value) => {
  if (value) {
    PlaybackStore.state.paused = true
    TranscriptStore.state.searchQuery = ''
  }
})

function setSelectedCues(start: TranscriptCue, end: TranscriptCue) {
  const { cues } = TranscriptStore.state
  const startIndex = cues.indexOf(start)
  const endIndex = cues.indexOf(end) + 1
  if (endIndex <= startIndex) {
    state.selectedCues = [start]
  } else {
    state.selectedCues = cues.slice(startIndex, endIndex).filter((c) => state.selectableCues?.includes(c))
  }
}

export function findPreviousCue(startCue: TranscriptCue, predicate: (cue: TranscriptCue) => boolean) {
  const { cues } = TranscriptStore.state
  return cues.slice(0, cues.indexOf(startCue)).reverse().find(predicate)
}

export function findNextCue(startCue: TranscriptCue, predicate: (cue: TranscriptCue) => boolean) {
  const { cues } = TranscriptStore.state
  return cues.slice(cues.indexOf(startCue) + 1).find(predicate)
}

export function isBoundaryCue(cue: TranscriptCue) {
  return cue.note && cue.note.id !== state.selectedNote.id && !isImmutable(cue.note.bookmark)
}

export function getFirstBoundaryCue() {
  if (state.selectedNote) {
    const cue = getFirstNoteCue(state.selectedNote)
    return findPreviousCue(cue, isBoundaryCue)
  }
}

export function getLastBoundaryCue() {
  if (state.selectedNote) {
    const cue = getFirstNoteCue(state.selectedNote)
    return findNextCue(cue, isBoundaryCue)
  }
}

AnnotationStore.on('set', (prop, value) => {
  const { cues } = TranscriptStore.state
  if (prop === 'startCue') {
    if (!state.selectedNote) {
      state.selectedNote = value.note
      const min = cues.indexOf(getFirstBoundaryCue()) + 1
      const max = cues.indexOf(getLastBoundaryCue())
      state.selectableCues = max > -1 ? cues.slice(min, max) : cues.slice(min)
    } else if (!value) {
      AnnotationStore.state.isUpdating = false // in case we need to trigger a re-render
      AnnotationStore.reset()
      return
    }
    setSelectedCues(value, getLastNoteCue(state.selectedNote))
  }
  if (prop === 'endCue') {
    setSelectedCues(state.startCue, value)
  }
})

AnnotationStore.use(useLogging('Annotation'))

export function isSelected(cue: TranscriptCue) {
  const { selectedCues } = state
  return !!selectedCues && state.selectedNote && selectedCues.includes(cue)
}

export function isFirstSelected(cue: TranscriptCue) {
  const { selectedCues } = state
  return cue === selectedCues[0]
}

export function isLastSelected(cue: TranscriptCue) {
  const { selectedCues } = state
  return cue === selectedCues[selectedCues.length - 1]
}

export function isSelectable(cue: TranscriptCue) {
  const { startCue, selectableCues } = state
  if (!startCue) {
    return false
  } else {
    return selectableCues && selectableCues.includes(cue)
  }
}

export function isNoteCue(cue: TranscriptCue) {
  if (cue.note && state.selectedNote && cue.note.id === state.selectedNote.id) {
    return isSelected(cue)
  } else {
    return !!cue.note
  }
}

export function isFirstNoteCue(cue: TranscriptCue) {
  if (isSelected(cue)) {
    return state.selectedCues[0] === cue
  } else {
    if (!isNoteCue(cue)) {
      return false
    }
    const first = TranscriptStore.state.cues.find((c) => hasMatchingNote(c, cue.note))
    return first === cue
  }
}

export function isLastNoteCue(cue: TranscriptCue) {
  if (isSelected(cue)) {
    return state.selectedCues.slice(-1)[0] === cue
  }
  if (!isNoteCue(cue)) {
    return false
  }
  const last = TranscriptStore.state.cues
    .slice()
    .reverse()
    .find((c) => hasMatchingNote(c, cue.note))
  return last === cue
}

export function isNameVisible(cue: TranscriptCue) {
  const { activeCue, transcript } = TranscriptStore.state
  const dialogue = transcript.find((cues) => cues.includes(cue))
  if (cue === activeCue) {
    // If cue is active, show name
    return true
  }
  if (dialogue.indexOf(cue) === 0 && !dialogue.includes(TranscriptStore.state.activeCue)) {
    // If cue is first in dialogue and dialogue doesn't include the activeCue
    return true
  }
  return false
}

export function isProcessingNote(note: INote) {
  return state.processingNote && state.processingNote.id === note.id
}

export function isProcessingCue(cue: TranscriptCue) {
  return state.processingCue ? state.processingCue.id === cue.id : state.processingNote && isSelected(cue)
}

export function hasMatchingNote(cue: TranscriptCue, note: INote) {
  return cue.note && cue.note.id === note?.id
}

export function getNoteForCue(cue: TranscriptCue) {
  if (state.selectedNote) {
    if (cue.note && cue.note.id === state.selectedNote.id) {
      return isSelected(cue) ? state.selectedNote : null
    } else {
      return isSelected(cue) ? state.selectedNote : cue.note
    }
  } else {
    return cue.note
  }
}

export function getFirstNoteCue(note: INote) {
  return TranscriptStore.state.cues.find((cue) => hasMatchingNote(cue, note))
}

export function getLastNoteCue(note: INote) {
  return TranscriptStore.state.cues
    .slice()
    .reverse()
    .find((cue) => hasMatchingNote(cue, note))
}

export function getNoteCues(note: INote) {
  return TranscriptStore.state.cues.filter((cue) => hasMatchingNote(cue, note))
}

// action items and bookmarks can get pre-prended to a speech bubble
// skip those if they're present and return the first speech cue
export function getFirstSpeakerCue(cues: TranscriptCue[]): TranscriptCue {
  for (const cue of cues) {
    if (cue.note && cue.note.bookmark.highlight) {
      return cue
    } else if (!cue.note) {
      return cue
    }
  }
  // shouldn't get here, but just in case
  return cues[0]
}
